// based on https://github.com/lannex/react-password-strength-bar
import type { CSSProperties, ReactNode } from 'react';
import React, { Fragment } from 'react';
import type { ZxcvbnResult } from '@zxcvbn-ts/core';
import { zxcvbnAsync, zxcvbnOptions, debounce } from '@zxcvbn-ts/core';
import zxcvbnCommonPackage from '@zxcvbn-ts/language-common';
import zxcvbnEnPackage from '@zxcvbn-ts/language-en';
import matcherPwnedFactory from '@zxcvbn-ts/matcher-pwned';

const options = {
  translations: zxcvbnEnPackage.translations,
  graphs: zxcvbnCommonPackage.adjacencyGraphs,
  dictionary: {
    ...zxcvbnCommonPackage.dictionary,
    ...zxcvbnEnPackage.dictionary,
  },
};

const matcherPwned = matcherPwnedFactory(fetch, zxcvbnOptions);
zxcvbnOptions.addMatcher('pwned', matcherPwned);
zxcvbnOptions.setOptions(options);

interface PasswordStrengthBarItemProps {
  score: number;
  itemNum: number;
  barColors: string[];
}

const itemStyle: CSSProperties = {
  flexBasis: 0,
  flexGrow: 1,
  position: 'relative',
  maxWidth: '100%',
  width: '100%',
  height: 2,
};

const Item: React.FunctionComponent<PasswordStrengthBarItemProps> = ({
  score,
  itemNum,
  barColors,
}) => {
  let bgColor = barColors[0];
  if (score >= itemNum) {
    bgColor = barColors[score];
  }

  return (
    <div
      style={{
        ...itemStyle,
        backgroundColor: bgColor,
      }}
    />
  );
};

export interface PasswordFeedback {
  warning?: string;
  suggestions?: string[];
}

interface PasswordStrengthBarState {
  score: number;
  result?: ZxcvbnResult;
}

export interface PasswordStrengthBarProps {
  className?: string;
  style?: CSSProperties;
  scoreWordClassName?: string;
  scoreWordStyle?: CSSProperties;
  password: string;
  userInputs?: string[];
  barColors?: string[];
  scoreWords?: ReactNode[];
  minLength?: number;
  shortScoreWord?: ReactNode;
  onChangeScore?: (
    score: PasswordStrengthBarState['score'],
    feedback: PasswordFeedback,
  ) => void;
}

const rootStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
};

const wrapStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  margin: '5px 0 0',
};

const spaceStyle: CSSProperties = {
  width: 4,
};

const descStyle: CSSProperties = {
  margin: '5px 0 0',
  color: '#898792',
  fontSize: 14,
  textAlign: 'right',
};

const defaultBarColors = ['#ddd', '#ef4836', '#f6b44d', '#2b90ef', '#25c281'];
const defaultScoreWords = ['weak', 'weak', 'okay', 'good', 'strong'];
const defaultMinLength = 8;

class PasswordStrengthBar extends React.Component<
  PasswordStrengthBarProps,
  PasswordStrengthBarState
> {
  public static defaultProps: PasswordStrengthBarProps = {
    className: undefined,
    style: undefined,
    scoreWordClassName: undefined,
    scoreWordStyle: undefined,
    password: '',
    userInputs: [],
    barColors: defaultBarColors,
    scoreWords: defaultScoreWords,
    minLength: defaultMinLength,
    shortScoreWord: 'too short',
    onChangeScore: undefined,
  };

  public state: PasswordStrengthBarState = {
    score: 0,
    result: undefined,
  };

  public componentDidMount(): void {
    void this.setScore();
  }

  public componentDidUpdate(prevProps: PasswordStrengthBarProps): void {
    const { password } = this.props;
    if (prevProps.password !== password) {
      this.setState({ result: undefined });
      void this.setScore();
    }
  }

  private setScore = debounce(
    async () => {
      const {
        password,
        minLength = defaultMinLength,
        userInputs,
        onChangeScore,
      } = this.props;
      if (password.length < minLength) {
        this.setState({
          score: 0,
          result: undefined,
        });
        return;
      }
      const result = await zxcvbnAsync(password, userInputs);
      const { score, feedback } = result;
      if (password !== this.props.password) {
        return;
      }
      this.setState(
        {
          score,
          result,
        },
        () => {
          if (onChangeScore) {
            onChangeScore(score, feedback);
          }
        },
      );
    },
    200,
    false,
  );

  public render(): ReactNode {
    const {
      className,
      style,
      scoreWordClassName,
      scoreWordStyle,
      password,
      barColors = defaultBarColors,
      scoreWords = defaultScoreWords,
      minLength = defaultMinLength,
      shortScoreWord,
    } = this.props;
    const { score } = this.state;
    const newShortScoreWord =
      password.length >= minLength ? scoreWords[score] : shortScoreWord;
    const feedback = this.state.result?.feedback;
    return (
      <div className={className} style={{ ...rootStyle, ...style }}>
        <div style={wrapStyle}>
          {[1, 2, 3, 4].map((el: number) => (
            <Fragment key={`password-strength-bar-item-${el}`}>
              {el > 1 && <div style={spaceStyle} />}
              <Item score={score} itemNum={el} barColors={barColors} />
            </Fragment>
          ))}
        </div>
        <p
          className={scoreWordClassName}
          style={{ ...descStyle, ...scoreWordStyle }}
        >
          {newShortScoreWord}
        </p>
        <p>{feedback?.warning}</p>
        <p>{feedback?.suggestions}</p>
      </div>
    );
  }
}

export default PasswordStrengthBar;
